import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * What's Next — where a loan stands, what blocks it, and whose turn it is.
 *
 * **One query and no mutations.** This tab derives everything from what the
 * other tabs already own and writes nothing; the server has no commit path in
 * the domain behind it, asserted by a test that reads the source. So there is
 * nothing here to invalidate and no tag to carry.
 *
 * One call rather than five, for the same reason the server assembles in one
 * pass: five requests would be five snapshots of a loan taken at five different
 * moments, and the tiles would disagree with the cards beneath them for reasons
 * nobody could reproduce.
 *
 * Because it is derived, it goes stale the moment somebody clears a condition
 * on another tab. `refetchOnMountOrArgChange` is what makes returning to this
 * tab show the file as it is now rather than as it was when it first loaded.
 */
const BASE = (loanId) => `/b2b/loan/applications/${loanId}/whats-next`;

/** The endpoint answers `{status, message, data}`. */
const unwrap = (response) => response?.data ?? null;

export const whatsNextAPI = createApi({
  reducerPath: 'whatsNext',
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  tagTypes: ['WhatsNext'],
  endpoints: (builder) => ({
    getWhatsNext: builder.query({
      query: ({ loanId }) => ({
        url: BASE(loanId),
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['WhatsNext'],
    }),
  }),
});

/**
 * Refetch What's Next after any of the user's own writes, on any tab.
 *
 * This slice is separate from the ones the tabs mutate through, so their
 * invalidations never reach it. Rather than list every mutation that could
 * complete a milestone (credit, AUS, documents, disclosures, conditions …),
 * every fulfilled mutation on every slice invalidates the tag. It only costs
 * a fetch while something is subscribed — the celebration provider on the
 * loan-file shell — and a burst of saves collapses into one refetch.
 */
const REFRESH_DEBOUNCE_MS = 800;
let refreshTimer = null;

export const whatsNextRefreshMiddleware = (storeApi) => (next) => (action) => {
  const result = next(action);
  const type = typeof action?.type === 'string' ? action.type : '';
  if (type.endsWith('/executeMutation/fulfilled') && !type.startsWith('whatsNext/')) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      storeApi.dispatch(whatsNextAPI.util.invalidateTags(['WhatsNext']));
    }, REFRESH_DEBOUNCE_MS);
  }
  return result;
};

export const { useGetWhatsNextQuery } = whatsNextAPI;
